/** Longest wait for the window `load` event (slow third-party scripts/images shouldn't hold the work back forever). */
const MAX_WAIT_FOR_LOAD_MS = 5_000
/** Longest wait for an idle period once loaded, on a page that never goes idle. */
const MAX_WAIT_FOR_IDLE_MS = 2_000

/**
 * Calls `callback` once the page has loaded and the browser is idle, so optional work (the 3D hero, analytics and
 * marketing scripts) doesn't compete with the page's own startup (LCP, hydration, Lighthouse TBT). Returns a cancel
 * function. Browsers without requestIdleCallback (Safari) get a short timeout instead.
 */
export const whenPageIdle = (callback: () => void): (() => void) => {
  let cancelled = false
  let idleHandle: number | undefined
  let timer: number | undefined
  let loadFallback: number | undefined

  const runWhenIdle = () => {
    window.removeEventListener("load", runWhenIdle)
    window.clearTimeout(loadFallback)
    if (cancelled) return
    if (typeof window.requestIdleCallback === "function") {
      idleHandle = window.requestIdleCallback(() => !cancelled && callback(), { timeout: MAX_WAIT_FOR_IDLE_MS })
    } else {
      timer = window.setTimeout(() => !cancelled && callback(), 200)
    }
  }

  if (document.readyState === "complete") runWhenIdle()
  else {
    window.addEventListener("load", runWhenIdle)
    loadFallback = window.setTimeout(runWhenIdle, MAX_WAIT_FOR_LOAD_MS)
  }

  return () => {
    cancelled = true
    window.removeEventListener("load", runWhenIdle)
    window.clearTimeout(loadFallback)
    window.clearTimeout(timer)
    if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle)
  }
}
